<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import AgentBotsAPI from 'dashboard/api/agentBots';
import {
  buildTemplateParameters,
  replaceTemplateVariables,
  findComponentByType,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
} from 'dashboard/helper/templateHelper';
import TemplatePickersToolbar from '../../TemplatePickers/TemplatePickersToolbar.vue';

const STATUS_OPTIONS = [
  { value: 'open', label: 'open' },
  { value: 'pending', label: 'pending' },
  { value: 'resolved', label: 'resolved' },
  { value: 'snoozed', label: 'snoozed' },
  { value: 'ai_pause', label: 'ai_pause' },
];

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  agentBotId: { type: Number, default: null },
  excludeSlotId: { type: [Number, String], default: null },
  allSlots: { type: Array, default: () => [] },
  apiSample: { type: [Object, Array, String, Number, Boolean], default: null },
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();

onMounted(() => {
  if (typeof store?.dispatch === 'function') {
    store.dispatch('inboxes/get');
  }
});

const config = computed({
  get: () => props.modelValue || {},
  set: value => emit('update:modelValue', value),
});

const update = patch => {
  config.value = { ...config.value, ...patch };
};

const cloudInboxes = computed(() => {
  const records = store?.getters?.['inboxes/getInboxes'] || [];
  return records.filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox?.provider === 'whatsapp_cloud'
  );
});

const selectedInbox = computed(() =>
  cloudInboxes.value.find(
    inbox => Number(inbox.id) === Number(config.value.fallback_inbox_id)
  )
);

const inboxTemplates = computed(() => {
  const inbox = selectedInbox.value;
  if (!inbox) return [];
  const templates = inbox.message_templates || inbox?.additional_attributes?.message_templates || [];
  if (!Array.isArray(templates)) return [];
  return templates.filter(t => t?.status?.toLowerCase() === 'approved' && Array.isArray(t.components));
});

// Match by name + language, but fall back to name-only so a stored/selected
// template still resolves when the language is missing or differs slightly
// (otherwise the picker silently clears and the variable fields never load).
const findTemplate = (name, language) => {
  if (!name) return null;
  return (
    inboxTemplates.value.find(
      t => t.name === name && t.language === language
    ) ||
    inboxTemplates.value.find(t => t.name === name) ||
    null
  );
};

const selectedTemplate = computed(() =>
  findTemplate(config.value.template_name, config.value.template_language)
);

const headerComponent = computed(() =>
  selectedTemplate.value
    ? findComponentByType(selectedTemplate.value, COMPONENT_TYPES.HEADER)
    : null
);

const hasMediaHeader = computed(() => {
  const header = headerComponent.value;
  return !!(header && MEDIA_FORMATS.includes(header.format));
});

const hasTextHeaderWithVars = computed(() => {
  const header = headerComponent.value;
  return !!(
    header &&
    header.format === 'TEXT' &&
    header.text &&
    /{{[^}]+}}/.test(header.text)
  );
});

const headerTextVarKeys = computed(() => {
  if (!hasTextHeaderWithVars.value) return [];
  const matches = headerComponent.value.text.match(/{{([^}]+)}}/g) || [];
  return matches.map(m => m.replace(/{{|}}/g, ''));
});

const bodyText = computed(() => {
  const body = selectedTemplate.value
    ? findComponentByType(selectedTemplate.value, COMPONENT_TYPES.BODY)
    : null;
  return body?.text || '';
});

const bodyVarKeys = computed(() => {
  const matches = bodyText.value.match(/{{([^}]+)}}/g) || [];
  return matches.map(m => m.replace(/{{|}}/g, ''));
});

const bodyVariables = computed(
  () => config.value.template_variables?.body || {}
);
const headerVariables = computed(
  () => config.value.template_variables?.header || {}
);

const previewBody = computed(() => {
  if (!bodyText.value) return '';
  return replaceTemplateVariables(bodyText.value, {
    body: bodyVariables.value,
  });
});

// Build the variables skeleton straight from the passed template so
// callers don't depend on `selectedTemplate`/computed properties (which
// reference props.modelValue and may be stale right after an emit).
const buildSkeletonForTemplate = (template, current = {}) => {
  if (!template) return {};

  const headerComp = findComponentByType(template, COMPONENT_TYPES.HEADER);
  const isMediaHeader = !!(
    headerComp && MEDIA_FORMATS.includes(headerComp.format)
  );
  const headerText = headerComp?.format === 'TEXT' ? headerComp.text || '' : '';
  const headerHasVars = /{{[^}]+}}/.test(headerText);
  const headerKeys = headerHasVars
    ? (headerText.match(/{{([^}]+)}}/g) || []).map(m => m.replace(/{{|}}/g, ''))
    : [];

  const skeleton = buildTemplateParameters(template, isMediaHeader);
  const merged = { body: {}, header: {} };

  Object.keys(skeleton.body || {}).forEach(key => {
    merged.body[key] = current?.body?.[key] ?? '';
  });

  if (isMediaHeader) {
    merged.header.media_url = current?.header?.media_url ?? '';
    merged.header.media_type = skeleton.header?.media_type || '';
    if ('media_name' in (skeleton.header || {})) {
      merged.header.media_name = current?.header?.media_name ?? '';
    }
  }

  if (headerHasVars) {
    headerKeys.forEach(key => {
      merged.header[key] = current?.header?.[key] ?? '';
    });
  }

  return merged;
};

const onInboxChange = event => {
  const value = event.target.value;
  const inboxId = value ? Number(value) : null;
  update({
    fallback_inbox_id: inboxId,
    template_name: null,
    template_language: null,
    template_variables: {},
  });
};

const onTemplateChange = event => {
  const value = event.target.value;
  if (!value) {
    update({
      template_name: null,
      template_language: null,
      template_variables: {},
    });
    return;
  }
  const [name, language] = value.split('||');
  const template = findTemplate(name, language);
  if (!template) return;

  const variables = buildSkeletonForTemplate(
    template,
    config.value.template_variables || {}
  );

  update({
    template_name: template.name,
    template_language: template.language,
    template_variables: variables,
  });
};

// Re-hydrate skeleton when the parent loads an existing automation
// (modelValue arrives async with template_name already set).
watch(
  () => selectedTemplate.value,
  template => {
    if (!template) return;
    const current = config.value.template_variables || {};
    const hasBodyKeys = current?.body && Object.keys(current.body).length > 0;
    const hasHeaderKeys =
      current?.header && Object.keys(current.header).length > 0;
    if (hasBodyKeys || hasHeaderKeys) return;
    update({ template_variables: buildSkeletonForTemplate(template, current) });
  },
  { immediate: true }
);

const setBodyVariable = (key, value) => {
  const next = {
    ...(config.value.template_variables || {}),
    body: {
      ...(config.value.template_variables?.body || {}),
      [key]: value,
    },
  };
  update({ template_variables: next });
};

const setHeaderVariable = (key, value) => {
  const next = {
    ...(config.value.template_variables || {}),
    header: {
      ...(config.value.template_variables?.header || {}),
      [key]: value,
    },
  };
  update({ template_variables: next });
};

const appendToField = (key, payload) => {
  const current = config.value[key] || '';
  update({ [key]: `${current}${payload.token}` });
};

const appendToBodyVariable = (key, payload) => {
  const current = bodyVariables.value[key] || '';
  setBodyVariable(key, `${current}${payload.token}`);
};

const appendToHeaderVariable = (key, payload) => {
  const current = headerVariables.value[key] || '';
  setHeaderVariable(key, `${current}${payload.token}`);
};

const showFallbackBlock = computed(() => cloudInboxes.value.length > 0);

const formatPlaceholder = key => `{{${key}}}`;

// ---------------- Post actions on the destination conversation ----------------
//
// When notify_whatsapp creates a brand-new conversation (e.g. for a leader
// reached via fallback inbox), the user can configure what happens to that
// conversation right after creation. These three knobs are stored under
// `config.post_actions` and are applied by Slots::NotifyWhatsappService.

const kanbanColumns = ref([]);
const isLoadingColumns = ref(false);

const postActions = computed(() => config.value.post_actions || {});
const showPostActions = ref(false);

const updatePostActions = patch => {
  const merged = { ...(config.value.post_actions || {}), ...patch };
  Object.keys(merged).forEach(key => {
    if (merged[key] === '' || merged[key] === null || merged[key] === undefined) {
      delete merged[key];
    }
  });
  update({ post_actions: merged });
};

const loadKanbanColumns = async () => {
  if (!props.agentBotId) {
    kanbanColumns.value = [];
    return;
  }
  isLoadingColumns.value = true;
  try {
    const { data } = await AgentBotsAPI.getKanbanColumns(props.agentBotId);
    kanbanColumns.value = Array.isArray(data) ? data : [];
  } catch (error) {
    kanbanColumns.value = [];
  } finally {
    isLoadingColumns.value = false;
  }
};

const hasMultipleBoards = computed(() => {
  const ids = new Set(kanbanColumns.value.map(c => c.board_id));
  return ids.size > 1;
});

const columnLabel = column => {
  if (hasMultipleBoards.value) {
    const prefix = column.inbox_name || column.board_name || `#${column.board_id}`;
    return `${prefix} » ${column.name}`;
  }
  return column.name;
};

onMounted(() => {
  loadKanbanColumns();
  if (Object.keys(postActions.value).length > 0) {
    showPostActions.value = true;
  }
});

watch(
  () => props.agentBotId,
  () => loadKanbanColumns()
);
</script>

<template>
  <div class="space-y-3">
    <div>
      <label class="block text-xs text-slate-500 mb-1">
        {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.PHONE', 'Telefone (E.164)') }}
      </label>
      <div class="flex items-center gap-1">
        <input
          :value="config.phone || ''"
          type="text"
          class="flex-1 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono"
          placeholder="+5511999998888"
          @input="update({ phone: $event.target.value })"
        />
        <TemplatePickersToolbar
          :agent-bot-id="agentBotId"
          :exclude-slot-id="excludeSlotId"
          :all-slots="allSlots"
          :api-sample="apiSample"
          @pick="appendToField('phone', $event)"
        />
      </div>
    </div>

    <div>
      <div class="flex items-center justify-between mb-1">
        <label class="block text-xs text-slate-500">
          {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.MESSAGE_TEMPLATE', 'Template da mensagem') }}
        </label>
        <TemplatePickersToolbar
          :agent-bot-id="agentBotId"
          :exclude-slot-id="excludeSlotId"
          :all-slots="allSlots"
          :api-sample="apiSample"
          @pick="appendToField('message_template', $event)"
        />
      </div>
      <textarea
        :value="config.message_template || ''"
        rows="3"
        class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono"
        placeholder="Novo pedido de {{contact.name}}: {{api.data.total}}"
        @input="update({ message_template: $event.target.value })"
      />
    </div>

    <div
      v-if="showFallbackBlock"
      class="mt-2 pt-3 border-t border-dashed border-slate-200 dark:border-slate-700 space-y-3"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-replace w-3.5 h-3.5 text-slate-500" />
        <h5 class="text-xs font-semibold text-slate-700 dark:text-slate-200">
          {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.FALLBACK_TITLE', 'Fallback (template Meta)') }}
        </h5>
      </div>
      <p class="text-[11px] text-slate-500 leading-snug">
        {{
          $t(
            'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.FALLBACK_HINT',
            'Em inboxes WhatsApp Cloud tentamos primeiro o envio livre. Se a Meta recusar (ex.: fora da janela de 24h), enviamos o template aprovado configurado abaixo.'
          )
        }}
      </p>

      <div>
        <label class="block text-xs text-slate-500 mb-1">
          {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.FALLBACK_INBOX', 'Inbox WhatsApp Cloud para fallback') }}
        </label>
        <select
          :value="config.fallback_inbox_id || ''"
          class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          @change="onInboxChange($event)"
        >
          <option value="">
            {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.NO_FALLBACK', 'Sem fallback (usar somente envio livre)') }}
          </option>
          <option
            v-for="inbox in cloudInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div v-if="selectedInbox">
        <label class="block text-xs text-slate-500 mb-1">
          {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.TEMPLATE', 'Template aprovado') }}
        </label>
        <select
          :value="
            config.template_name
              ? `${config.template_name}||${config.template_language || ''}`
              : ''
          "
          class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          @change="onTemplateChange($event)"
        >
          <option value="">
            {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.SELECT_TEMPLATE', '-- selecione um template --') }}
          </option>
          <option
            v-for="t in inboxTemplates"
            :key="`${t.name}||${t.language}`"
            :value="`${t.name}||${t.language}`"
          >
            {{ t.name }} ({{ t.language }})
          </option>
        </select>
        <p
          v-if="!inboxTemplates.length"
          class="mt-1 text-[11px] text-amber-600 dark:text-amber-400"
        >
          {{
            $t(
              'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.NO_TEMPLATES',
              'Nenhum template aprovado encontrado para esta inbox. Sincronize os templates nas configurações da inbox.'
            )
          }}
        </p>
      </div>

      <div
        v-if="selectedTemplate"
        class="space-y-3 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-2"
      >
        <div>
          <div class="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.PREVIEW', 'Pré-visualização') }}
          </div>
          <div class="text-xs whitespace-pre-wrap font-mono text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 rounded p-2 border border-slate-200 dark:border-slate-700">
            {{ previewBody }}
          </div>
        </div>

        <div v-if="hasTextHeaderWithVars">
          <div class="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.HEADER_VARS', 'Variáveis do cabeçalho') }}
          </div>
          <div
            v-for="key in headerTextVarKeys"
            :key="`header-${key}`"
            class="space-y-1"
          >
            <label class="block text-[11px] text-slate-500 font-mono">{{ formatPlaceholder(key) }}</label>
            <div class="flex items-center gap-1">
              <input
                :value="headerVariables[key] || ''"
                type="text"
                class="flex-1 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono"
                placeholder="{{contact.name}}"
                @input="setHeaderVariable(key, $event.target.value)"
              />
              <TemplatePickersToolbar
                :agent-bot-id="agentBotId"
                :exclude-slot-id="excludeSlotId"
                :all-slots="allSlots"
                :api-sample="apiSample"
                @pick="appendToHeaderVariable(key, $event)"
              />
            </div>
          </div>
        </div>

        <div v-if="hasMediaHeader">
          <div class="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.HEADER_MEDIA', 'Cabeçalho (mídia)') }}
          </div>
          <div class="space-y-1">
            <label class="block text-[11px] text-slate-500">URL da mídia</label>
            <div class="flex items-center gap-1">
              <input
                :value="headerVariables.media_url || ''"
                type="text"
                class="flex-1 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono"
                placeholder="https://..."
                @input="setHeaderVariable('media_url', $event.target.value)"
              />
              <TemplatePickersToolbar
                :agent-bot-id="agentBotId"
                :exclude-slot-id="excludeSlotId"
                :all-slots="allSlots"
                :api-sample="apiSample"
                @pick="appendToHeaderVariable('media_url', $event)"
              />
            </div>
            <div
              v-if="'media_name' in headerVariables"
              class="space-y-1 mt-1"
            >
              <label class="block text-[11px] text-slate-500">Nome do arquivo (opcional)</label>
              <input
                :value="headerVariables.media_name || ''"
                type="text"
                class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono"
                placeholder="documento.pdf"
                @input="setHeaderVariable('media_name', $event.target.value)"
              />
            </div>
          </div>
        </div>

        <div v-if="bodyVarKeys.length">
          <div class="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.BODY_VARS', 'Variáveis do corpo') }}
          </div>
          <div class="space-y-2">
            <div
              v-for="key in bodyVarKeys"
              :key="`body-${key}`"
              class="space-y-1"
            >
              <label class="block text-[11px] text-slate-500 font-mono">{{ formatPlaceholder(key) }}</label>
              <div class="flex items-center gap-1">
                <input
                  :value="bodyVariables[key] || ''"
                  type="text"
                  class="flex-1 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono"
                  placeholder="{{contact.name}}"
                  @input="setBodyVariable(key, $event.target.value)"
                />
                <TemplatePickersToolbar
                  :agent-bot-id="agentBotId"
                  :exclude-slot-id="excludeSlotId"
                  :all-slots="allSlots"
                  :api-sample="apiSample"
                  @pick="appendToBodyVariable(key, $event)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <p
      v-else
      class="text-[11px] text-slate-500 italic"
    >
      {{
        $t(
          'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.NO_CLOUD_INBOX',
          'Nenhuma inbox WhatsApp Cloud configurada. O envio será sempre via Evolution.'
        )
      }}
    </p>

    <div class="mt-2 pt-3 border-t border-dashed border-slate-200 dark:border-slate-700">
      <button
        type="button"
        class="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:text-woot-600"
        @click="showPostActions = !showPostActions"
      >
        <span
          :class="
            showPostActions
              ? 'i-lucide-chevron-down'
              : 'i-lucide-chevron-right'
          "
          class="w-3.5 h-3.5"
        />
        {{
          $t(
            'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_ACTIONS_TITLE',
            'Após criar a conversa do destinatário'
          )
        }}
      </button>
      <p
        v-if="showPostActions"
        class="mt-1 text-[11px] text-slate-500 leading-snug"
      >
        {{
          $t(
            'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_ACTIONS_HINT',
            'Aplicado apenas à nova conversa criada pelo envio (não afeta a conversa de origem). Tudo é opcional.'
          )
        }}
      </p>

      <div
        v-if="showPostActions"
        class="mt-3 space-y-3"
      >
        <div>
          <label class="block text-xs text-slate-500 mb-1">
            {{
              $t(
                'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_MOVE_COLUMN',
                'Mover card para coluna'
              )
            }}
          </label>
          <select
            :value="postActions.move_to_column_id || ''"
            class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            :disabled="isLoadingColumns || !kanbanColumns.length"
            @change="
              updatePostActions({
                move_to_column_id: $event.target.value
                  ? Number($event.target.value)
                  : null,
              })
            "
          >
            <option value="">
              {{
                $t(
                  'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_NO_MOVE',
                  '— não mover —'
                )
              }}
            </option>
            <option
              v-for="column in kanbanColumns"
              :key="column.id"
              :value="column.id"
            >
              {{ columnLabel(column) }}
            </option>
          </select>
          <p
            v-if="!isLoadingColumns && !kanbanColumns.length"
            class="mt-1 text-[11px] text-slate-500"
          >
            {{
              $t(
                'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_NO_COLUMNS',
                'Nenhuma coluna disponível para o bot atual.'
              )
            }}
          </p>
          <p
            v-if="postActions.move_to_column_id"
            class="mt-1 text-[11px] text-slate-500"
          >
            {{
              $t(
                'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_MOVE_HINT',
                'Se a coluna não existir mais, a movimentação é ignorada silenciosamente.'
              )
            }}
          </p>
        </div>

        <div>
          <label class="block text-xs text-slate-500 mb-1">
            {{
              $t(
                'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_CHANGE_STATUS',
                'Alterar status'
              )
            }}
          </label>
          <select
            :value="postActions.change_status || ''"
            class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            @change="
              updatePostActions({ change_status: $event.target.value || null })
            "
          >
            <option value="">
              {{
                $t(
                  'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_NO_STATUS',
                  '— manter status atual —'
                )
              }}
            </option>
            <option
              v-for="opt in STATUS_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-xs text-slate-500 mb-1">
            {{
              $t(
                'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_ADD_LABEL',
                'Atribuir etiqueta'
              )
            }}
          </label>
          <input
            :value="postActions.add_label || ''"
            type="text"
            class="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono"
            placeholder="lider-aviso"
            @input="
              updatePostActions({ add_label: $event.target.value || null })
            "
          />
          <p class="mt-1 text-[11px] text-slate-500">
            {{
              $t(
                'OMNI.SLOTS.API_AUTOMATIONS.NOTIFY_WHATSAPP.POST_LABEL_HINT',
                'Criada automaticamente se ainda não existir.'
              )
            }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
